import {Table, Button, Image} from "antd";
import React from "react";
import {EmptyMessage} from "components/_defaultComponents/EmptyMessage/EmptyMessage";
import {Link} from "react-router-dom";
import {ConditionalTooltip} from "components/_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {truncateString} from "utils/helpers";
import {useStore} from "hooks/useStore";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import moment from "moment";
import {
    creditLogTransactionTypes,
    creditLogTransactionTypeOptions,
    creditLogSubTypes,
    creditLogSubTypeOptions,
    invoiceStatuses,
    providerCompanies,
    getCreditModeLabel
} from "constants/constants";

/** "vion" → "Vion Kurye"; eşleşme yoksa gelen değeri, o da yoksa "-" */
const getProviderCompanyLabel = value =>
    providerCompanies.find(o => o.value === value)?.name || value || "-";

const getTransactionTypeLabel = value =>
    creditLogTransactionTypeOptions.find(o => o.value === value)?.label;
const getTransactionTypeColor = value =>
    creditLogTransactionTypeOptions.find(o => o.value === value)?.color;
const getSubTypeLabel = value => creditLogSubTypeOptions.find(o => o.value === value)?.label;
const getTransactionTypeTextColor = value =>
    creditLogTransactionTypeOptions.find(o => o.value === value)?.textColor;

/** null / undefined → "-", 0 dahil sayısal değerler → "X TL" */
const formatAmountTl = value => (value == null ? "-" : `${value} TL`);

const amountColor = value => (value == null ? undefined : value > 0 ? "green" : "red");

export const CreditLogTableTab = ({
    listQueryKey,
    paymentTransactionLogs,
    paymentTransactionLogsLoading,
    subCompanyId,
    selectedTransactionType,
    selectedSubType,
    handleObjection,
    pageParam,
    setPageParam
}) => {
    const stores = useStore();

    const rows = paymentTransactionLogs?.data?.rows ?? [];

    return (
        <Table
            key={listQueryKey}
            rowKey={(_, index) => String(index)}
            dataSource={paymentTransactionLogsLoading ? [] : rows}
            loading={paymentTransactionLogsLoading}
            scroll={{x: "max-content"}}
            pagination={{
                total: paymentTransactionLogs?.data?.dataCount,
                current: pageParam,
                pageSize: 10,
                hideOnSinglePage: true,
                showSizeChanger: false,
                onChange: page => {
                    setPageParam(page);
                }
            }}
            locale={{
                emptyText: <EmptyMessage />
            }}
            expandable={{
                expandedRowRender: record => {
                    return (
                        <>
                            {record.subTransactions && record.subTransactions?.length > 0 ? (
                                <Table
                                    dataSource={record.subTransactions}
                                    pagination={false}
                                    size="small"
                                    showHeader={false}
                                    rowClassName={(record, index) => {
                                        return "gray-background";
                                    }}
                                >
                                    <Table.Column width={100} />
                                    {stores.meStore.checkRole("hideForOperationRole") && (
                                        <Table.Column width={50} />
                                    )}
                                    {!subCompanyId && <Table.Column width={200} />}
                                    {!stores.meStore.checkRole(
                                        "showCreateCreditLogObjectionButton"
                                    ) && <Table.Column width={50} />}
                                    <Table.Column
                                        key="subType"
                                        dataIndex="subType"
                                        width={150}
                                        maxWidth={150}
                                        title={"Ücret Tipi"}
                                        render={subType => (
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    fontWeight: "bold",
                                                    marginLeft:
                                                        subType === creditLogSubTypes.OBJECTION
                                                            ? "0px"
                                                            : "20px"
                                                }}
                                            >
                                                {getSubTypeLabel(subType) || subType}
                                            </div>
                                        )}
                                    />
                                    <Table.Column width={30} />
                                    {!stores.meStore.checkRole(
                                        "showCreateCreditLogObjectionButton"
                                    ) && <Table.Column width={50} />}
                                    {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                                        <Table.Column
                                            key="courierAmount"
                                            width={150}
                                            maxWidth={150}
                                            dataIndex="courierAmount"
                                            title={"Kurye Fiyatı"}
                                            render={(courierAmount, record) => (
                                                <div
                                                    style={{
                                                        fontSize: "12px",
                                                        fontWeight: "bold",
                                                        paddingLeft:
                                                            record?.subType ===
                                                            creditLogSubTypes.OBJECTION
                                                                ? "0px"
                                                                : "16px",
                                                        color: amountColor(courierAmount)
                                                    }}
                                                >
                                                    {formatAmountTl(courierAmount)}
                                                </div>
                                            )}
                                        />
                                    )}
                                    <Table.Column
                                        key="clientAmount"
                                        dataIndex="clientAmount"
                                        style={{paddingLeft: "20px !important"}}
                                        width={150}
                                        maxWidth={150}
                                        title={
                                            !stores.meStore.checkRole("notVisibleForCompanyUser")
                                                ? "Fiyat"
                                                : "Müşteri Fiyatı"
                                        }
                                        render={(clientAmount, record) => (
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    fontWeight: "bold",
                                                    paddingLeft:
                                                        record?.subType ===
                                                        creditLogSubTypes.OBJECTION
                                                            ? "0px"
                                                            : "16px",
                                                    color: amountColor(clientAmount)
                                                }}
                                            >
                                                {formatAmountTl(clientAmount)}
                                            </div>
                                        )}
                                    />
                                    <Table.Column width={220} />
                                    <Table.Column width={200} />
                                    <Table.Column width={200} />
                                    {!stores.meStore.checkRole("isCompanyUser") && (
                                        <Table.Column width={200} />
                                    )}
                                    {/* Sağlayıcı Firma sütunu için hizalama boşluğu */}
                                    <Table.Column width={150} />
                                    {/* Kredi modu sütunu için hizalama boşluğu */}
                                    <Table.Column width={60} />
                                    <Table.Column width={60} />
                                </Table>
                            ) : record.transactionPhoto ? (
                                <div>
                                    <h2>Fatura Görüntüsü </h2>
                                    {record.transactionPhoto.includes(".pdf") ||
                                    record.transactionPhoto.includes("buluttahsilat") ? (
                                        <a
                                            href={record.transactionPhoto}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <Button type="primary">Dekont Görüntüle</Button>
                                        </a>
                                    ) : (
                                        <Image
                                            src={record.transactionPhoto}
                                            width={200}
                                            height={200}
                                            style={{
                                                objectFit: "contain"
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                "Fatura Görüntüsü Bulunamadı"
                            )}
                        </>
                    );
                },
                rowExpandable: record =>
                    (record.subTransactions && record.subTransactions?.length > 0) ||
                    (record?.processedSubType === creditLogSubTypes.PAYMENT &&
                        record?.transactionType === creditLogTransactionTypes.LOAD)
            }}
        >
            <Table.Column
                dataIndex="forecastDate"
                title={"Tarih"}
                width={150}
                maxWidth={150}
                render={date => <span>{moment(date).format("DD.MM.YYYY")}</span>}
            />
            <Table.Column
                dataIndex="transactionType"
                title={"İşlem Türü"}
                width={200}
                maxWidth={200}
                render={(transactionType, row) => (
                    <div>
                        <span
                            style={{
                                backgroundColor: getTransactionTypeColor(transactionType),
                                padding: 5,
                                color: getTransactionTypeTextColor(transactionType),
                                borderRadius: 5
                            }}
                        >
                            {getTransactionTypeLabel(transactionType) ||
                                getTransactionTypeLabel(creditLogTransactionTypes.USE)}
                        </span>
                    </div>
                )}
            />
            <Table.Column
                dataIndex="processedSubType"
                title={"Ücret Tipi"}
                width={200}
                maxWidth={200}
                render={processedSubType => (
                    <span>{getSubTypeLabel(processedSubType) || processedSubType}</span>
                )}
            />
            {stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                <Table.Column
                    dataIndex="courierAmount"
                    title={"Kurye Fiyatı"}
                    width={150}
                    maxWidth={150}
                    render={courierAmount => (
                        <spann
                            style={
                                courierAmount == null
                                    ? undefined
                                    : courierAmount > 0
                                    ? {color: "green"}
                                    : {color: "red"}
                            }
                        >
                            {formatAmountTl(courierAmount)}
                        </spann>
                    )}
                />
            ) : (
                <></>
            )}
            <Table.Column
                dataIndex="clientAmount"
                title={"Müşteri Fiyatı"}
                width={150}
                maxWidth={150}
                render={clientAmount => (
                    <span
                        style={
                            clientAmount == null
                                ? undefined
                                : clientAmount > 0
                                ? {color: "green"}
                                : {color: "red"}
                        }
                    >
                        {formatAmountTl(clientAmount)}
                    </span>
                )}
            />
            {!stores.meStore.checkRole("isCompanyUser") && (
                <Table.Column
                    dataIndex="poolName"
                    title={"Proje Adı"}
                    width={200}
                    maxWidth={200}
                    render={(poolName, record) => (
                        <span>{record?.projectName ? record?.projectName : "-"}</span>
                    )}
                />
            )}
            <Table.Column
                dataIndex="vigoOrderNumber"
                width={200}
                maxWidth={200}
                title={"Sipariş Numarası"}
                render={(_, record) => (
                    <span>
                        {record?.order !== null ? (
                            record?.order?.vigoOrderNumber ? (
                                <Link
                                    target="_blank"
                                    data-testid="orderNumber"
                                    style={{maxWidth: "470px"}}
                                    to={{
                                        pathname:
                                            (stores.meStore.checkRole("isUatPoolCustomer")
                                                ? "/musteri/siparislerim/"
                                                : "/orders/") + record?.deliveryId
                                    }}
                                >
                                    {record?.order?.vigoOrderNumber}
                                </Link>
                            ) : (
                                <ConditionalTooltip
                                    condition={
                                        record?.order?.orderNumber &&
                                        truncateString(record?.order?.orderNumber, 16).indexOf(
                                            "..."
                                        ) > 0
                                    }
                                    text={record?.order?.orderNumber}
                                    child={
                                        <Link
                                            target="_blank"
                                            data-testid="orderNumber"
                                            style={{maxWidth: "470px"}}
                                            to={{
                                                pathname:
                                                    (stores.meStore.checkRole("isUatPoolCustomer")
                                                        ? "/musteri/siparislerim/"
                                                        : "/orders/") + record?.deliveryId
                                            }}
                                        >
                                            {record?.order?.orderNumber
                                                ? truncateString(record?.order?.orderNumber, 16)
                                                : "-"}
                                        </Link>
                                    }
                                />
                            )
                        ) : (
                            "-"
                        )}
                    </span>
                )}
            />
            <Table.Column
                dataIndex="invoiceStatus"
                title={"Fatura Durumu"}
                width={150}
                maxWidth={150}
                render={invoiceStatus => (
                    <span>
                        {invoiceStatuses.find(status => status.value === invoiceStatus)?.name ||
                            "-"}
                    </span>
                )}
            />
            <Table.Column
                key="courier"
                dataIndex="courier"
                title={"Kurye"}
                width={150}
                maxWidth={150}
                render={courier => (
                    <span>
                        {courier &&
                            (stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                                <Link
                                    target="_blank"
                                    to={{
                                        pathname: "/couriers/" + courier?.id
                                    }}
                                >
                                    {courier?.fullname || courier?.name}
                                </Link>
                            ) : (
                                courier?.fullname || courier?.name || "Kurye"
                            ))}
                    </span>
                )}
            />
            <Table.Column
                dataIndex="hasWalletTransaction"
                title={"Cüzdanda Var Mı?"}
                width={150}
                maxWidth={150}
                render={hasWalletTransaction => (
                    <span>
                        {hasWalletTransaction === true ? (
                            <span style={{color: "green", fontWeight: "bold"}}>Evet</span>
                        ) : (
                            <span style={{color: "red", fontWeight: "bold"}}>Hayır</span>
                        )}
                    </span>
                )}
            />
            <Table.Column
                dataIndex="providerCompany"
                title={"Sağlayıcı Firma"}
                width={150}
                maxWidth={150}
                render={(providerCompany, record) => (
                    <span>{getProviderCompanyLabel(record?.courier?.providerCompany)}</span>
                )}
            />
            <Table.Column
                dataIndex="creditMode"
                title={"Kredi Modu"}
                width={60}
                maxWidth={60}
                render={(creditMode, record) => (
                    <span>{record?.creditMode ? getCreditModeLabel(record?.creditMode) : "-"}</span>
                )}
            />
            {stores.meStore.checkRole("showCreateCreditLogObjectionButton") ? (
                selectedTransactionType?.includes(creditLogTransactionTypes.LOAD) &&
                selectedSubType?.includes(creditLogSubTypes.PAYMENT) ? null : (
                    !stores.meStore.checkRole("hideForOperationRole") && (
                        <Table.Column
                            dataIndex="transactionType"
                            title={"İtiraz Bildir"}
                            width={150}
                            render={(_, record) => {
                                if (
                                    record.transactionType !== creditLogTransactionTypes.LOAD &&
                                    record.transactionType !==
                                        creditLogTransactionTypes.WALLET_RESET &&
                                    !record.paymentObjection
                                ) {
                                    return stores.meStore.checkRole("createCreditLogObjection") &&
                                        stores.meStore.checkPermission(
                                            "creditLogs.createObjection"
                                        ) ? (
                                        <CustomButton
                                            onClick={() =>
                                                handleObjection(
                                                    record?.order?._id,
                                                    record?.deliveryId,
                                                    record?.subcompanyId
                                                )
                                            }
                                            style={{borderRadius: "10px"}}
                                        >
                                            İtiraz Bildir <ExclamationCircleOutlined />
                                        </CustomButton>
                                    ) : (
                                        <></>
                                    );
                                }
                                if (record.paymentObjection) {
                                    const {status} = record.paymentObjection;

                                    if (status === "pending") {
                                        return (
                                            <CustomButton
                                                style={{
                                                    backgroundColor: "#ff9f43",
                                                    color: "white",
                                                    borderRadius: "10px"
                                                }}
                                                disabled
                                            >
                                                İnceleniyor
                                            </CustomButton>
                                        );
                                    }

                                    if (status === "approved") {
                                        return (
                                            <CustomButton
                                                style={{
                                                    backgroundColor: "#00b894",
                                                    color: "white",
                                                    borderRadius: "10px"
                                                }}
                                                disabled
                                            >
                                                Onaylandı
                                            </CustomButton>
                                        );
                                    }

                                    if (status === "rejected") {
                                        return (
                                            <CustomButton
                                                style={{
                                                    backgroundColor: "#ff5354",
                                                    color: "white",
                                                    borderRadius: "10px"
                                                }}
                                                disabled
                                            >
                                                Reddedildi
                                            </CustomButton>
                                        );
                                    }
                                }
                                return null;
                            }}
                        />
                    )
                )
            ) : (
                <></>
            )}
        </Table>
    );
};

const styles = {
    loadContainer: {
        backgroundColor: "#009e0f",
        padding: 5,
        color: "white",
        borderRadius: 5
    },
    useContainer: {
        backgroundColor: "#dc2626",
        padding: 5,
        color: "white",
        borderRadius: 5
    },
    objectionContainer: {
        backgroundColor: "#990000",
        padding: 5,
        color: "white",
        borderRadius: 5
    },
    walletResetContainer: {
        backgroundColor: "#c27ba0",
        padding: 5,
        color: "white",
        borderRadius: 5
    },
    adjustmentContainer: {
        backgroundColor: "#ffe599",
        padding: 5,
        color: "white",
        borderRadius: 5
    }
};
